import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, errMsg, newIdemKey, type Card, type Category, type Charge, type ChargeResult, type CounterpartyPage, type Scope } from "../api";
import { formatMoney, normalizeMoneyInput } from "../lib/money";
import { useMe } from "../session";
import { AllocationField, allocError, saveAllocations, type AllocValue } from "./AllocationField";
import { Modal } from "./Modal";
import { ScopeDialog, useRepeat } from "./Recurrence";
import { Btn, Field, Input, Notice, Select } from "./ui";

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

/** Compra no cartão: única, parcelada ou fixa. Compra de série pergunta "somente esta / esta e as seguintes" ao salvar. */
export function ChargeForm({ card, charge, onClose, onDone }: { card: Card; charge: Charge | null; onClose: () => void; onDone: (msg: string) => void }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const [cats, setCats] = useState<Category[]>([]);
  const [cps, setCps] = useState<CounterpartyPage["items"]>([]);
  const [description, setDescription] = useState(charge?.description ?? "");
  const [amount, setAmount] = useState(charge ? charge.amount.decimal.replace(".", ",") : "");
  const [purchaseDate, setPurchaseDate] = useState(charge?.purchaseDate ?? today());
  const [categoryId, setCategoryId] = useState(charge?.categoryId ?? "");
  const [counterpartyId, setCounterpartyId] = useState(charge?.counterpartyId ?? "");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [alloc, setAlloc] = useState<AllocValue | null>(null);
  const [askScope, setAskScope] = useState(false);
  const askRef = useRef(false); askRef.current = askScope;
  const closeForm = useCallback(() => { if (!askRef.current) onClose(); }, [onClose]);
  const rep = useRepeat();
  const idem = useRef(newIdemKey());
  const locked = !!charge && BigInt(charge.paid.cents) > 0n;   // compra já paga numa fatura: só descrição, beneficiário e observações

  useEffect(() => {
    void Promise.all([
      api<Category[]>("GET", `/api/${realm}/categories?status=ativa`),
      api<CounterpartyPage>("GET", `/api/${realm}/counterparties?status=ativa&limit=200&role=fornecedor`),
    ]).then(([c, p]) => { setCats(c); setCps(p.items); }).catch((e) => setErr(errMsg(e)));
  }, [realm]);
  const options = useMemo(() => {
    const byId = new Map(cats.map((c) => [c.id, c]));
    const path = (c: Category): string => (c.parentId && byId.get(c.parentId) ? `${path(byId.get(c.parentId)!)} › ${c.name}` : c.name);
    return cats.filter((c) => c.level >= 2 && c.nature === "despesa" && !c.systemKey).map((c) => ({ id: c.id, label: path(c) })).sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [cats]);

  async function submit(e: FormEvent | null, chosen?: Scope) {
    e?.preventDefault();
    setErr(null);
    const norm = normalizeMoneyInput(amount);
    if (norm === null || norm.startsWith("-") || /^0+(\.0+)?$/.test(norm)) return setErr("Informe um valor maior que zero, por exemplo 1.234,56.");
    if (!categoryId) return setErr("Escolha a categoria.");
    if (!description.trim()) return setErr("Informe a descrição.");
    let repeat: Record<string, unknown> | undefined;
    if (!charge) { const b = rep.build(); if (b.error) return setErr(b.error); repeat = b.repeat; }
    const ae = allocError(alloc); if (ae) return setErr(ae);
    if (charge?.seriesId && !chosen) { setAskScope(true); return; }
    setBusy(true);
    try {
      if (charge) {
        const body: Record<string, unknown> = { version: charge.version, description, counterpartyId: counterpartyId || null, ...(notes.trim() ? { notes: notes.trim() } : {}) };
        if (!locked) Object.assign(body, { amount: norm, purchaseDate, categoryId });
        if (charge.seriesId && chosen === "following") body.scope = "following";
        const r = await api<ChargeResult>("PATCH", `/api/${realm}/card-charges/${charge.id}`, body);
        if (alloc) await saveAllocations(realm, "card_charge", charge.id, alloc);
        onDone(`Compra atualizada${r.affected ? ` (e ${r.affected} seguintes)` : ""}.`);
      } else {
        const r = await api<ChargeResult>("POST", `/api/${realm}/cards/${card.id}/charges`, {
          description, amount: norm, purchaseDate, categoryId, counterpartyId: counterpartyId || null, notes: notes.trim() || null, ...(repeat ? { repeat } : {}),
        }, idem.current);
        if (alloc && !r.series) await saveAllocations(realm, "card_charge", r.id, alloc);
        onDone(r.series ? `${r.series.created} ${r.series.kind === "parcelada" ? "parcelas lançadas" : "compras fixas lançadas"} nas faturas correspondentes.` : "Compra lançada na fatura.");
      }
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); setAskScope(false); }
  }

  return (
    <>
      <Modal title={charge ? "Editar compra" : `Nova compra — ${card.name}`} onClose={closeForm} wide>
        <form onSubmit={(e) => void submit(e)} noValidate data-charge-form>
          {err && <Notice kind="error">{err}</Notice>}
          {locked && <Notice>Esta compra já foi paga (total ou em parte) numa fatura: valor, data e categoria estão travados.</Notice>}
          <Field label="Descrição" required><Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} required autoComplete="off" name="description" /></Field>
          <div className="grid2">
            <Field label="Valor (R$)" required><Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" decimals={2} placeholder="0,00" name="amount" disabled={locked} /></Field>
            <Field label="Data da compra" required><Input type="date" value={purchaseDate} max={today()} onChange={(e) => setPurchaseDate(e.target.value)} name="purchaseDate" disabled={locked} /></Field>
          </div>
          <div className="grid2">
            <Field label="Categoria" required>
              <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} name="category" disabled={locked}>
                <option value="">Selecione…</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </Select>
            </Field>
            <Field label="Beneficiário"><Select value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)} name="counterparty"><option value="">—</option>{cps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
          </div>
          {!charge && rep.fields({ norm: normalizeMoneyInput(amount), format: (v) => formatMoney(v.toFixed(2), "BRL") })}
          {charge?.seriesId && <p className="rep-info" data-scope-box>{charge.seriesKind === "parcelada" ? `Parcela ${charge.installmentNo}/${charge.installmentTotal}` : "Compra fixa"} — ao salvar, vamos perguntar se a alteração vale só para esta ou também para as seguintes.</p>}
          {(charge || rep.mode === "unica") && <AllocationField realm={realm} entityId={card.entityId} ownerType="card_charge" ownerId={charge?.id} value={alloc} setValue={setAlloc} />}
          <Field label="Observações"><Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} name="notes" /></Field>
          <p className="small muted">A compra entra na fatura pelo ciclo do cartão (fecha dia {card.closingDay}, vence dia {card.dueDay}). A despesa só é contada nos relatórios quando a fatura for paga.</p>
          <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><button type="submit" className="btn primary" disabled={busy} data-charge-save>{busy ? "Salvando…" : "Salvar"}</button></div>
        </form>
      </Modal>
      {askScope && <ScopeDialog title="Aplicar a alteração a…" what="Esta compra faz parte de uma série. A alteração vale somente para ela ou também para as seguintes ainda não pagas?" busy={busy} onClose={() => setAskScope(false)} onPick={(sc) => void submit(null, sc)} />}
    </>
  );
}
